import React, { useRef } from 'react';
import { Box, Button, FormHelperText, Typography } from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PartnerLogoAvatar from '../../Partner/widgets/PartnerLogoAvatar';
import CoverImageOptimizingBlock from '../../Events/ManageEvent/components/CoverImageOptimizingBlock';
import CompanyInfoLabel from './CompanyInfoLabel';
import { companyInfoOutlinedButtonSx, companyInfoUploadButtonSx } from './companyInfoStyles';
import { PARTNER_MEDIA_ACCEPT } from './companyInfoDefaults';

const BANNER_WIDTH = 340;

const CompanyMediaField = ({
  label,
  required = false,
  previewUrl,
  variant,
  placeholderLetter = 'P',
  previewAlt = 'Company logo',
  formatHint,
  outputHint,
  uploadDisabled = false,
  deleteDisabled = false,
  error = false,
  helperText,
  optimizing = false,
  optimizationError = '',
  onImageSelected,
  onCancelOptimization,
  onDelete,
}) => {
  const inputRef = useRef(null);
  const isLogo = variant === 'logo';
  const showOptimizingBlock = optimizing || Boolean(optimizationError);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) {
      void onImageSelected?.(file);
    }
  };

  return (
    <Box sx={{ mb: 3.25 }}>
      <CompanyInfoLabel required={required}>{label}</CompanyInfoLabel>
      {formatHint ? (
        <Typography sx={{ fontSize: '0.71875rem', color: '#9B9590', mb: outputHint ? 0.25 : 1.25 }}>
          {formatHint}
        </Typography>
      ) : null}
      {outputHint ? (
        <Typography sx={{ fontSize: '0.71875rem', color: '#9B9590', mb: 1.25 }}>
          {outputHint}
        </Typography>
      ) : null}

      {showOptimizingBlock ? (
        <CoverImageOptimizingBlock
          compact
          error={optimizationError}
          onCancel={onCancelOptimization}
          onRetry={optimizationError ? onImageSelected : undefined}
          accept={PARTNER_MEDIA_ACCEPT}
          sx={{
            width: isLogo ? 220 : BANNER_WIDTH,
            minHeight: isLogo ? 220 : 200,
            borderRadius: isLogo ? '16px' : '12px',
          }}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {isLogo ? (
            <PartnerLogoAvatar
              src={previewUrl}
              alt={previewAlt}
              initial={placeholderLetter}
              shape="circle"
              size={96}
              sx={{ fontSize: '1.75rem', border: 'none', boxShadow: 'none' }}
            />
          ) : (
            <Box
              sx={{
                width: { xs: '100%', sm: BANNER_WIDTH },
                maxWidth: '100%',
                aspectRatio: '1536 / 1024',
                flexShrink: 0,
                borderRadius: '12px',
                overflow: 'hidden',
                bgcolor: '#F7F5F3',
                border: error ? '1px solid #d32f2f' : '1px dashed #E5E1DD',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {previewUrl ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt=""
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              ) : (
                <Typography
                  sx={{ fontSize: '0.8125rem', color: '#9B9590', fontWeight: 500, px: 2 }}
                >
                  Cover banner
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: 180, flexShrink: 0 }}>
            <input
              ref={inputRef}
              type="file"
              accept={PARTNER_MEDIA_ACCEPT}
              hidden
              onChange={handleFileChange}
            />
            <Button
              variant="contained"
              disableElevation
              disabled={uploadDisabled}
              startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={() => inputRef.current?.click()}
              sx={companyInfoUploadButtonSx}
            >
              {previewUrl ? 'Replace' : 'Upload New'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              onClick={onDelete}
              disabled={deleteDisabled}
              sx={companyInfoOutlinedButtonSx}
            >
              Delete
            </Button>
          </Box>
        </Box>
      )}
      {helperText && !showOptimizingBlock ? (
        <FormHelperText error={error} sx={{ mx: 0, mt: 0.75 }}>
          {helperText}
        </FormHelperText>
      ) : null}
    </Box>
  );
};

export default CompanyMediaField;
